import { ReactElement, createContext, useContext, useMemo } from 'react';
import { useActiveWorkspace } from '../store/workspace/workspace.hooks';
import { logger } from '../util/logger';
import { DefaultRouteBuilder } from './route.builder';
import {
  BeaconRouterContextProviderProps,
  BeaconRouterContextValue,
  RouteBuilder,
} from './route.types';

const BeaconRouterContext = createContext<BeaconRouterContextValue | undefined>(
  undefined
);

const BeaconRouterContextProvider = (
  props: BeaconRouterContextProviderProps
): ReactElement | null => {
  const { children, routeBuilderFactory } = props;

  const workspace = useActiveWorkspace();

  const value = useMemo<BeaconRouterContextValue>(() => {
    let builder: RouteBuilder = new DefaultRouteBuilder({
      prefix: `/${workspace?.tenant?.id}`,
    });

    if (routeBuilderFactory) {
      builder = routeBuilderFactory();
    }

    const performWindowNavigation = async (url: string) => {
      if (!window.location.pathname.includes(url)) {
        logger.info('navigating to ' + url);
        window.location.href = url;
      }
    };

    return {
      builder,
      navigateToLogout: async () => {
        const url = builder.getAuthLogoutRoute();
        performWindowNavigation(url);
      },
      performWindowNavigation,
    };
  }, [workspace, routeBuilderFactory]);

  return (
    <BeaconRouterContext.Provider value={value}>
      {children}
    </BeaconRouterContext.Provider>
  );
};

const useBeaconRouter = (): BeaconRouterContextValue => {
  const val = useContext(BeaconRouterContext);
  return val as BeaconRouterContextValue;
};

export { useBeaconRouter, BeaconRouterContextProvider };
